import { makeStyles } from '@material-ui/core/styles';
import {
  Grid,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CardActions,
} from '@material-ui/core';
import {
  HomePageCompanyLogo,
  HomePageStarredEntities,
  HomePageToolkit,
  WelcomeTitle,
} from '@backstage/plugin-home';
import { HomePageSearchBar } from '@backstage/plugin-search';
import {
  Content,
  ContentHeader,
  InfoCard,
  Link,
} from '@backstage/core-components';
import HomeIcon from '@material-ui/icons/Home';
import CategoryIcon from '@material-ui/icons/Category';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import MapIcon from '@material-ui/icons/MyLocation';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import NotificationsIcon from '@material-ui/icons/Notifications';
import { BrandStripe } from '../../theme/BrandStripe';
import { zambianColors } from '../../theme/zambianTheme';
import { pluginCatalog } from './pluginCatalog';
import { usePlatformStats } from './usePlatformStats';
import { RecentActivityFeed } from './RecentActivityFeed';

const useStyles = makeStyles(theme => ({
  searchBar: {
    display: 'flex',
    maxWidth: '60vw',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
    padding: '8px 0',
    borderRadius: '50px',
    margin: 'auto',
  },
  statCard: {
    textAlign: 'center',
    padding: theme.spacing(2.5),
    height: '100%',
    borderTop: `4px solid ${zambianColors.orange}`,
  },
  statValue: {
    fontSize: '2.25rem',
    fontWeight: 800,
    color: zambianColors.greenDark,
    lineHeight: 1,
  },
  statLabel: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    fontSize: '0.75rem',
  },
  pluginCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[6],
    },
  },
  pluginIcon: {
    color: zambianColors.orangeDark,
    fontSize: '2rem',
    marginBottom: theme.spacing(1),
  },
  sectionHeading: {
    fontWeight: 700,
    color: zambianColors.greenDark,
  },
  brandWrap: {
    marginBottom: theme.spacing(2),
  },
}));

const StatCard = ({
  value,
  label,
  loading,
}: {
  value: number;
  label: string;
  loading: boolean;
}) => {
  const classes = useStyles();
  return (
    <Card className={classes.statCard} elevation={1}>
      <Typography className={classes.statValue}>
        {loading ? '—' : value.toLocaleString()}
      </Typography>
      <Typography className={classes.statLabel}>{label}</Typography>
    </Card>
  );
};

const toolkitTools = [
  { url: '/catalog', label: 'Catalog', icon: <CategoryIcon /> },
  { url: '/create', label: 'Create', icon: <CreateComponentIcon /> },
  { url: '/docs', label: 'TechDocs', icon: <LibraryBooks /> },
  { url: '/api-docs', label: 'APIs', icon: <ExtensionIcon /> },
  { url: '/tech-radar', label: 'Tech Radar', icon: <MapIcon /> },
  {
    url: '/notifications',
    label: 'Notifications',
    icon: <NotificationsIcon />,
  },
];

export const HomePage = () => {
  const classes = useStyles();
  const stats = usePlatformStats();

  return (
    <Content>
      <Box className={classes.brandWrap}>
        <BrandStripe />
      </Box>

      {/* Welcome */}
      <Grid container spacing={3} alignItems="center">
        <Grid item xs={12} md={3}>
          <HomePageCompanyLogo />
        </Grid>
        <Grid item xs={12} md={9}>
          <WelcomeTitle
            language={[
              'Welcome',
              'Mwauka bwanji',
              'Mwapoleni',
              'Mwabuka buti',
              'Mwadzuka bwanji',
            ]}
          />
          <Typography variant="subtitle1" color="textSecondary">
            Ministry of Health · Open Health, Open Data, Open Zambia
          </Typography>
        </Grid>
      </Grid>

      {/* Search */}
      <Box mt={3} mb={4}>
        <HomePageSearchBar
          classes={{ root: classes.searchBar }}
          placeholder="Search catalog, docs and APIs…"
        />
      </Box>

      {/* Stats */}
      <ContentHeader title="Platform at a glance" />
      <Grid container spacing={3}>
        <Grid item xs={6} md={3}>
          <StatCard
            value={stats.components}
            label="Components"
            loading={stats.loading}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard value={stats.apis} label="APIs" loading={stats.loading} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            value={stats.resources}
            label="Resources"
            loading={stats.loading}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            value={stats.templates}
            label="Templates"
            loading={stats.loading}
          />
        </Grid>
      </Grid>

      {/* Two-column body */}
      <Box mt={5}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <InfoCard title="Your starred entities">
              <HomePageStarredEntities />
            </InfoCard>
            <Box mt={3}>
              <InfoCard title="Recent catalog activity">
                <RecentActivityFeed limit={6} />
              </InfoCard>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <HomePageToolkit title="Toolkit" tools={toolkitTools} />
            <Box mt={3}>
              <InfoCard title="Quick links">
                {pluginCatalog.slice(0, 6).map(p => (
                  <Box key={p.id} py={0.5}>
                    <Link to={p.route}>
                      <Box
                        display="flex"
                        alignItems="center"
                        style={{ gap: 8 }}
                      >
                        <HomeIcon
                          fontSize="small"
                          style={{ color: zambianColors.greenDark }}
                        />
                        <span>{p.title}</span>
                      </Box>
                    </Link>
                  </Box>
                ))}
              </InfoCard>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Plugin grid */}
      <Box mt={5}>
        <ContentHeader title="Tools on the platform" />
        <Grid container spacing={3}>
          {pluginCatalog.map(p => {
            const Icon = p.icon;
            return (
              <Grid item xs={12} sm={6} md={3} key={p.id}>
                <Card className={classes.pluginCard}>
                  <CardContent style={{ flex: 1 }}>
                    <Icon className={classes.pluginIcon} />
                    <Typography variant="h6" style={{ fontWeight: 700 }}>
                      {p.title}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {p.tagline}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Link to={p.route} style={{ textDecoration: 'none' }}>
                      <Button color="primary" size="small">
                        Open →
                      </Button>
                    </Link>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Content>
  );
};
